import styled, { css } from 'styled-components';
import { PrimaryButton, SecondaryButton } from '../Button';
import { builderInfoWidthLookup } from './constants';
import { IBuilderInfoKind } from './types';
import { theme } from '../../styles/theme';
import {
  borderedContent,
  overflowingStyles,
} from '../../styles/shareableStyles';
import ThinDivider from '../ThinDivider';
import ScrollableWrapper from '../ScrollableWrapper';

export const contentBasePadding = css`
  padding: 8px 12px;
`;

export const Container = styled.div<{ $kind: IBuilderInfoKind }>`
  display: flex;
  flex-direction: column;
  max-height: 350px;
  width: ${({ $kind }) => builderInfoWidthLookup[$kind]}px;
  overflow-x: hidden;
  font-size: 10px;
  line-height: normal;
  color: ${theme.colors.secondary800};
  > :not(:first-child) {
    border-top: 1px solid ${theme.colors.border};
  }
`;

export const BorderedInfo = styled.div`
  flex: 0 0 auto;
  min-height: 20px;
  min-width: 20px;
  margin-right: 6px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  font-weight: 500;
  color: ${theme.colors.secondary900};
  background-color: ${theme.colors.inverse};
  ${borderedContent};
`;

export const HighlightedLabel = styled.div`
  font-weight: 500;
  color: ${theme.colors.secondary900};
`;

export const BorderedSection = styled.div`
  ${borderedContent};
`;

export const BorderedSectionHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-height: 26px;
  padding: 4px 8px;
  border-bottom: 1px solid ${theme.colors.border};
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  background-color: ${theme.colors.tableHeader};
`;

export const StyledPopupLabel = styled.div<{ $isDisabled?: boolean }>`
  flex: 0 0 auto;
  padding: 1px 3px;
  border-radius: 2px;
  max-width: 240px;
  ${overflowingStyles};
  background-color: ${theme.colors.blue500};
  color: ${theme.colors.secondary900};

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      color: ${theme.colors.secondary400};
    `}
`;

export const VersionedEntityPopupLabel = styled.div<{
  $isDisabled?: boolean;
  $isLatestVersion?: boolean;
}>`
  flex: 0 0 auto;
  display: inline-block;
  padding: 1px 3px;
  border-radius: 2px;
  background-color: ${theme.colors.tableHeader};
  font-weight: 500;
  word-break: break-word;
  color: ${({ $isLatestVersion }) =>
    $isLatestVersion ? theme.colors.secondary900 : theme.colors.secondary800};
  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      text-decoration: line-through;
    `}
`;

export const FooterRow = styled.div`
  flex: 1 0 100%;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  margin-top: 12px;
`;

export const PopupButton = styled(PrimaryButton)`
  border-radius: 16px;
`;

export const PopupSecondaryButton = styled(SecondaryButton)`
  border-radius: 16px;
`;

export const PlainInfo = styled.div`
  display: flex;
  align-items: center;
`;

export const InfoLabel = styled.div<{ $noMargin?: boolean }>`
  ${({ $noMargin }) => !$noMargin && `margin-right: 4px`};
`;

export const PlainValue = styled.div`
  font-weight: 500;
  color: ${theme.colors.mainBlue};
`;

export const Content = styled.div`
  ${contentBasePadding};
`;

export const MainContent = styled(ScrollableWrapper)`
  flex: 1;
  row-gap: 8px;
  ${contentBasePadding};
`;

export const GridColumns = styled.div`
  display: grid;
  grid-template-columns: minmax(128px, auto) 1fr;
  grid-column-gap: 8px;
  align-items: start;
`;

export const HorizontalRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

export const HeaderContainer = styled(HorizontalRow)`
  flex-wrap: wrap;
  row-gap: 4px;
`;

export const VerticalRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`;

export const VerticalNestedRow = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 4px;
`;

export const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
`;

export const VerticalSeparator = styled(ThinDivider).attrs(() => ({
  layout: 'vertical',
  size: 'short',
}))``;

export const FooterContainer = styled(Content)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

export const FooterContainerLeft = styled(Content)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;
